/* src/apiClient.js
   Hash House Yoga — 5DD App API client
   Replaces supabaseClient.js — lightweight fetch-based API client.
   Shared auth via n1-universal-auth-token — same key as N1Yoga + HHY Journal.
   If a user is already logged into N1Yoga or HHY Journal they are automatically
   authenticated here (cross-app session sharing by design).
*/

const API_BASE = '/api';
const AUTH_KEY = 'n1-universal-auth-token';

// ─── Token Management ───────────────────────────────────────────────────────

function getToken() {
  const stored = localStorage.getItem(AUTH_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    return parsed.access_token || parsed;
  } catch {
    return stored;
  }
}

function setToken(tokenData) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(tokenData));
}

function clearToken() {
  localStorage.removeItem(AUTH_KEY);
}

// ─── Core API Caller ────────────────────────────────────────────────────────

export async function api(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    body: options.body instanceof FormData
      ? options.body
      : options.body ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 401) {
    const data = await res.json().catch(() => ({}));
    if (data.code === 'TOKEN_EXPIRED') {
      const refreshed = await refreshToken();
      if (refreshed) {
        headers.Authorization = `Bearer ${getToken()}`;
        const retry = await fetch(`${API_BASE}${path}`, { ...options, headers });
        if (!retry.ok) {
          const errData = await retry.json().catch(() => ({ error: retry.statusText }));
          throw new Error(errData.error || `API error ${retry.status}`);
        }
        return retry.json();
      }
      clearToken();
      window.location.href = '/api/auth/google?redirect=/';
      return;
    }
    throw new Error(data.error || 'Unauthorized');
  }

  if (!res.ok) {
    const errData = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(errData.error || `API error ${res.status}`);
  }

  if (res.status === 204) return null;

  return res.json();
}

// ─── Token Refresh ──────────────────────────────────────────────────────────

async function refreshToken() {
  try {
    const stored = JSON.parse(localStorage.getItem(AUTH_KEY) || 'null');
    if (!stored?.refresh_token) return false;

    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: stored.refresh_token }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    // Merge so refresh_token + user survive if server omits them
    setToken({ ...stored, ...data });
    return true;
  } catch {
    return false;
  }
}

// ─── Auth ───────────────────────────────────────────────────────────────────

export const auth = {
  /** Get current session (validates token server-side) */
  getSession: async () => {
    const token = getToken();
    if (!token) return { user: null };
    try {
      return await api('/auth/session');
    } catch {
      return { user: null };
    }
  },

  /** Check if user has a stored token (quick sync check, no network) */
  hasToken: () => !!getToken(),

  /** Listen for auth changes (replacement for supabase.auth.onAuthStateChange) */
  onAuthStateChange: (callback) => {
    const token = getToken();
    if (token) {
      auth.getSession().then(session => {
        callback('SIGNED_IN', session);
      }).catch(() => {
        callback('SIGNED_OUT', { user: null });
      });
    } else {
      callback('SIGNED_OUT', { user: null });
    }

    const handler = (e) => {
      if (e.key === AUTH_KEY) {
        if (e.newValue) {
          auth.getSession().then(session => callback('SIGNED_IN', session));
        } else {
          callback('SIGNED_OUT', { user: null });
        }
      }
    };
    window.addEventListener('storage', handler);

    return { data: { subscription: { unsubscribe: () => window.removeEventListener('storage', handler) } } };
  },

  /** Get the user's Google access token (for Calendar/Tasks API) */
  getGoogleToken: () => api('/auth/google-token'),
};

// ─── Convenience Auth Exports ────────────────────────────────────────────────

export const signInWithGoogle = async () => {
  window.location.href = `${API_BASE}/auth/google?redirect=/`;
  return { data: {}, error: null };
};

export const signOut = async () => {
  clearToken();
  localStorage.removeItem('google_provider_token');
  window.location.href = '/';
  return { error: null };
};

// ─── Database ────────────────────────────────────────────────────────────────

export const db = {
  // Journal entries (5DD course journal — also shows personal entries with include_5dd)
  journal: {
    list:   (params)     => api(`/journal/entries${toQuery({ include_5dd: 'true', ...params })}`),
    get:    (id)         => api(`/journal/entries/${id}`),
    create: (body)       => api('/journal/entries', { method: 'POST', body }),
    update: (id, body)   => api(`/journal/entries/${id}`, { method: 'PATCH', body }),
    delete: (id)         => api(`/journal/entries/${id}`, { method: 'DELETE' }),
  },

  // Courses
  courses: {
    active:    ()          => api('/courses/active'),
    history:   ()          => api('/courses/history'),
    create:    (body)      => api('/courses', { method: 'POST', body }),
    update:    (id, body)  => api(`/courses/${id}`, { method: 'PATCH', body }),
    delete:    (id)        => api(`/courses/${id}`, { method: 'DELETE' }),
  },

  // Daily tasks
  tasks: {
    list:   (params)     => api(`/courses/tasks${toQuery(params)}`),
    upsert: (body)       => api('/courses/tasks', { method: 'PUT', body }),
  },

  // Daily habits
  habits: {
    list:   (params)     => api(`/courses/habits${toQuery(params)}`),
    upsert: (body)       => api('/courses/habits', { method: 'PUT', body }),
  },

  // Meals
  meals: {
    list:   (params)     => api(`/meals${toQuery(params)}`),
    get:    (id)         => api(`/meals/${id}`),
    create: (body)       => api('/meals', { method: 'POST', body }),
    update: (id, body)   => api(`/meals/${id}`, { method: 'PATCH', body }),
    delete: (id)         => api(`/meals/${id}`, { method: 'DELETE' }),
  },

  // Profiles
  profiles: {
    me:     ()           => api('/profiles/me'),
    update: (body)       => api('/profiles/me', { method: 'PATCH', body }),
  },

  // Course history
  courseHistory: {
    list:   ()           => api('/profiles/course-history'),
    update: (id, body)   => api(`/profiles/course-history/${id}`, { method: 'PATCH', body }),
  },

  // User variables (Signal biometrics — shared)
  variables: {
    list:   (params)     => api(`/variables${toQuery(params)}`),
    upsert: (body)       => api('/variables', { method: 'PUT', body }),
    delete: (id)         => api(`/variables/${id}`, { method: 'DELETE' }),
  },

  // Usage metrics (analytics)
  metrics: {
    create: (body)       => api('/metrics', { method: 'POST', body }),
    list:   (params)     => api(`/metrics${toQuery(params)}`),
  },

  // Leads
  leads: {
    create: (body)       => api('/leads', { method: 'POST', body }),
  },
};

// ─── Storage ─────────────────────────────────────────────────────────────────

export const storage = {
  /** Upload a file. Returns { path, filename, signedUrl } */
  upload: async (bucket, file, options = {}) => {
    const product = options.product || 'hhy';
    const formData = new FormData();
    formData.append('file', file);
    return api(`/storage/upload/${product}/${bucket}`, {
      method: 'POST',
      body: formData,
    });
  },

  /** Get a signed URL for an existing file */
  getSignedUrl: (path, expiresIn) => {
    return api('/storage/signed-url', {
      method: 'POST',
      body: { path, expiresIn },
    });
  },
};

// ─── Functions ───────────────────────────────────────────────────────────────

export const functions = {
  /** Transcribe personal journal audio */
  transcribePersonal: (body) => api('/functions/transcribe-personal', { method: 'POST', body }),

  /** Transcribe 5DD journal audio */
  transcribeAudio: (body) => api('/functions/transcribe-audio', { method: 'POST', body }),

  /** Analyze meal photo (OpenAI Vision) */
  analyzeMeal: (body) => api('/functions/analyze-meal', { method: 'POST', body }),

  /** Synthesize course narrative (OpenAI) */
  synthesizeCourse: (body) => api('/functions/synthesize-course', { method: 'POST', body }),

  /** Calendar assistant */
  calendarAssistant: (body) => api('/functions/n1-ca-executor', { method: 'POST', body }),
};

// ─── Query String Helper ──────────────────────────────────────────────────────

function toQuery(params) {
  if (!params || Object.keys(params).length === 0) return '';
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `?${qs}` : '';
}

export default { api, auth, db, storage, functions, signInWithGoogle, signOut };
